import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { ArrowUpRight, MapPin, CalendarDays, Globe2, Sparkles } from "lucide-react";
import { formatDate, eventPriceRange } from "@/lib/format";

export default function Discover() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        endpoints
            .discover()
            .then((d) => {
                setData(d);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-24">
                <div className="font-mono text-sm text-[#666]" data-testid="discover-loading">
                    Loading discovery feed…
                </div>
            </div>
        );
    }

    if (!data) return null;

    return (
        <div data-testid="discover-page" className="pm-fade">
            {/* HERO */}
            <section className="border-b border-[#2a2a2a]">
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 pt-10 md:pt-16 pb-10 md:pb-20">
                    <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-[#666]">
                        <span className="w-6 h-px bg-[#666]" /> Discover · Feb 2026
                    </div>
                    <h1 className="mt-4 font-display font-black uppercase leading-[0.9] tracking-tighter text-[#f4f4f0] text-5xl md:text-7xl lg:text-8xl">
                        A new sound
                        <br />
                        <span className="text-[#ff4400]">from every</span>
                        <br />
                        street corner.
                    </h1>
                    <p className="mt-8 max-w-2xl text-[#a3a3a3] leading-relaxed">
                        Fan-first discovery for artists across Ethiopia, Latin America, and the world.
                        Follow, register, and show up. Payments happen offline — the music happens here.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        <Link to="/artists" className="pm-btn pm-btn-primary" data-testid="hero-cta-artists">
                            Browse Artists
                        </Link>
                        <Link to="/events" className="pm-btn" data-testid="hero-cta-events">
                            Upcoming Events
                        </Link>
                    </div>
                </div>
            </section>

            {/* Featured artists — Bento */}
            <section className="border-b border-[#2a2a2a]">
                <SectionHeader
                    kicker="01 · Featured"
                    title="Artists in rotation"
                    right={
                        <Link to="/artists" className="text-xs font-mono uppercase tracking-widest text-[#a3a3a3] hover:text-[#ff4400] flex items-center gap-1">
                            All artists <ArrowUpRight className="w-3 h-3" />
                        </Link>
                    }
                />
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 pb-10 md:pb-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
                    {data.featured_artists.slice(0, 6).map((a, i) => (
                        <Link
                            key={a.id}
                            to={`/artists/${a.id}`}
                            data-testid={`featured-artist-${a.id}`}
                            className={`group relative bg-[#0a0a0a] overflow-hidden ${
                                i === 0 ? "col-span-2 row-span-2 md:col-span-2 md:row-span-2 aspect-square md:aspect-auto" : "aspect-square"
                            }`}
                        >
                            <img
                                src={a.image_url}
                                alt=""
                                className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500 ease-out"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                            <div className="relative h-full w-full p-4 md:p-6 flex flex-col justify-end">
                                <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">
                                    {a.genre}
                                </div>
                                <div className={`font-display font-black uppercase tracking-tight text-[#f4f4f0] mt-1 leading-none ${i === 0 ? "text-3xl md:text-5xl" : "text-lg md:text-2xl"}`}>
                                    {a.name}
                                </div>
                                <div className="mt-2 text-xs text-[#a3a3a3] flex items-center gap-1">
                                    <MapPin className="w-3 h-3" /> {a.city}, {a.country}
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* Upcoming events */}
            <section className="border-b border-[#2a2a2a]">
                <SectionHeader
                    kicker="02 · Live"
                    title="Upcoming events"
                    right={
                        <Link to="/events" className="text-xs font-mono uppercase tracking-widest text-[#a3a3a3] hover:text-[#ff4400] flex items-center gap-1">
                            All events <ArrowUpRight className="w-3 h-3" />
                        </Link>
                    }
                />
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 pb-10 md:pb-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
                    {data.upcoming_events.slice(0, 6).map((e) => (
                        <EventTile key={e.id} event={e} />
                    ))}
                </div>
            </section>

            {/* Regions */}
            <section className="border-b border-[#2a2a2a]">
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-10 md:py-16 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
                    <RegionColumn
                        kicker="Ethiopia"
                        title="From Addis to Bahir Dar"
                        artists={data.ethiopia_artists}
                    />
                    <RegionColumn
                        kicker="Latin America"
                        title="Continent-wide sound"
                        artists={data.latin_artists}
                    />
                </div>
            </section>

            {/* Browse by */}
            <section>
                <SectionHeader kicker="03 · Browse" title="By city, country, genre" />
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 pb-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
                    <BrowseColumn icon={MapPin} label="Cities" items={data.cities} type="city" />
                    <BrowseColumn icon={Globe2} label="Countries" items={data.countries} type="country" />
                    <BrowseColumn icon={Sparkles} label="Genres" items={data.genres} type="genre" />
                </div>
            </section>
        </div>
    );
}

function SectionHeader({ kicker, title, right }) {
    return (
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 pt-10 md:pt-14 pb-6 flex items-end justify-between gap-4">
            <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">
                    {kicker}
                </div>
                <h2 className="mt-1 font-display font-black uppercase tracking-tighter text-[#f4f4f0] text-2xl md:text-4xl">
                    {title}
                </h2>
            </div>
            {right}
        </div>
    );
}

function EventTile({ event }) {
    return (
        <Link
            to={`/events/${event.id}`}
            data-testid={`upcoming-event-${event.id}`}
            className="group relative bg-[#0a0a0a] aspect-[4/3] overflow-hidden"
        >
            <img
                src={event.image_url}
                alt=""
                className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
            <div className="relative h-full w-full p-5 flex flex-col justify-between">
                <div className="flex items-center justify-between">
                    <div className="bg-[#ff4400] px-2 py-1 text-[10px] font-mono uppercase tracking-widest text-white">
                        {formatDate(event.date)}
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#f4f4f0]">
                        {eventPriceRange(event)}
                    </div>
                </div>
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3]">
                        {event.artist_name}
                    </div>
                    <div className="font-display font-black uppercase tracking-tight text-[#f4f4f0] text-xl md:text-2xl leading-tight mt-1">
                        {event.name}
                    </div>
                    <div className="mt-2 text-xs text-[#a3a3a3] flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {event.venue_name} · {event.city}, {event.country}
                    </div>
                </div>
            </div>
        </Link>
    );
}

function RegionColumn({ kicker, title, artists }) {
    return (
        <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">
                {kicker}
            </div>
            <h3 className="mt-1 font-display font-black uppercase tracking-tight text-2xl md:text-3xl">
                {title}
            </h3>
            <div className="mt-6 border-t border-[#2a2a2a]">
                {artists.map((a) => (
                    <Link
                        key={a.id}
                        to={`/artists/${a.id}`}
                        data-testid={`region-artist-${a.id}`}
                        className="flex items-center gap-4 py-4 border-b border-[#2a2a2a] group"
                    >
                        <img
                            src={a.image_url}
                            alt=""
                            className="w-14 h-14 object-cover grayscale group-hover:grayscale-0 transition-all"
                        />
                        <div className="flex-1 min-w-0">
                            <div className="font-display font-bold text-lg leading-tight group-hover:text-[#ff4400] transition-colors">
                                {a.name}
                            </div>
                            <div className="text-xs text-[#a3a3a3] mt-1">
                                {a.genre} · {a.city}
                            </div>
                        </div>
                        <ArrowUpRight className="w-4 h-4 text-[#666] group-hover:text-[#ff4400]" />
                    </Link>
                ))}
            </div>
        </div>
    );
}

function BrowseColumn({ icon: Icon, label, items, type }) {
    return (
        <div className="bg-[#0a0a0a] p-6">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#666]">
                <Icon className="w-3 h-3" /> {label}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
                {items.map((it) => (
                    <Link
                        key={it}
                        to={`/${type === "genre" ? "artists" : type === "city" ? "artists" : "artists"}?${type}=${encodeURIComponent(it)}`}
                        data-testid={`browse-${type}-${it}`}
                        className="px-3 py-2 border border-[#2a2a2a] text-xs font-mono uppercase tracking-wide text-[#f4f4f0] hover:border-[#ff4400] hover:text-[#ff4400] transition-colors"
                    >
                        {it}
                    </Link>
                ))}
            </div>
        </div>
    );
}
